import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2, RotateCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  isInsightBotConfigured,
  loginInsightBot,
  chatWithInsightBot,
  resetInsightBotSession,
} from '../../services/insightBotApi';
import useAuthStore from '../../store/authStore';
import './InsightBotWidget.css';

const WELCOME_MESSAGE = {
  role: 'bot',
  content: 'Hi! Ask me anything about your data.',
};

const BOT_DISPLAY_NAME = 'Genie';

const ALLOWED_ROLES = ['Super Admin', 'PMO Team', 'RMG Lead'];

const STORAGE_KEY_MESSAGES = 'insightBotMessages';
const STORAGE_KEY_SESSION_ID = 'insightBotSessionId';

function loadStoredMessages() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MESSAGES);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function loadStoredSessionId() {
  try {
    return localStorage.getItem(STORAGE_KEY_SESSION_ID) || null;
  } catch {
    return null;
  }
}

const THINKING_STAGES = [
  { afterSeconds: 0, text: 'Thinking...' },
  { afterSeconds: 10, text: 'Analyzing your question...' },
  { afterSeconds: 30, text: 'Querying the data...' },
  { afterSeconds: 60, text: 'Still working on it, this can take a few minutes for larger datasets...' },
  { afterSeconds: 180, text: 'Almost there, crunching a large amount of data...' },
];

function getThinkingText(elapsedSeconds) {
  let text = THINKING_STAGES[0].text;
  for (const stage of THINKING_STAGES) {
    if (elapsedSeconds >= stage.afterSeconds) {
      text = stage.text;
    }
  }
  return text;
}

const STORAGE_KEY_POSITION = 'insightBotPosition';

function loadStoredPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_POSITION);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function InsightBotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadStoredMessages() || [WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingElapsed, setLoadingElapsed] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState(() => loadStoredPosition() || { bottom: 24, right: 24 });
  const messagesEndRef = useRef(null);
  const sessionIdRef = useRef(loadStoredSessionId());
  const widgetRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, bottom: 0, right: 0 });

  const persistSessionId = (sessionId) => {
    sessionIdRef.current = sessionId;
    try {
      if (sessionId) {
        localStorage.setItem(STORAGE_KEY_SESSION_ID, sessionId);
      } else {
        localStorage.removeItem(STORAGE_KEY_SESSION_ID);
      }
    } catch {
      // localStorage unavailable (e.g. private browsing) - continue without persistence
    }
  };

  const persistPosition = (newPosition) => {
    try {
      localStorage.setItem(STORAGE_KEY_POSITION, JSON.stringify(newPosition));
    } catch {
      // localStorage unavailable - continue without persistence
    }
  };

  const handleFabMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    const rect = widgetRef.current.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      bottom: position.bottom,
      right: position.right,
    };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      const newBottom = Math.max(24, dragStartRef.current.bottom - deltaY);
      const newRight = Math.max(24, dragStartRef.current.right - deltaX);

      setPosition({ bottom: newBottom, right: newRight });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      persistPosition({ bottom: position.bottom, right: position.right });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, position]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_MESSAGES, JSON.stringify(messages));
    } catch {
      // localStorage unavailable or quota exceeded - continue without persistence
    }
  }, [messages]);

  const configured = isInsightBotConfigured();
  const userRoles = useAuthStore((state) => state.user?.roles) || [];
  const isAllowedRole = userRoles.some((r) => ALLOWED_ROLES.includes(r));

  useEffect(() => {
    if (!isOpen || !configured || isReady) return;

    loginInsightBot()
      .then(() => setIsReady(true))
      .catch(() => {
        setMessages((prev) => [
          ...prev,
          { role: 'bot', content: `Unable to connect to ${BOT_DISPLAY_NAME}. Please try again later.` },
        ]);
      });
  }, [isOpen, configured, isReady]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  useEffect(() => {
    if (!isLoading) {
      setLoadingElapsed(0);
      return;
    }

    const intervalId = setInterval(() => {
      setLoadingElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isLoading]);

  if (!configured || !isAllowedRole) return null;

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const sendQuestion = async (question) => {
    if (!question || isLoading) return;

    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await chatWithInsightBot({
        message: question,
        sessionId: sessionIdRef.current,
      });
      const data = response.data;

      if (data?.session_id) {
        persistSessionId(data.session_id);
      }

      if (!data?.success || data?.error) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            content: data?.error || 'Sorry, I could not process that question. Please try again.',
          },
        ]);
        return;
      }

      setMessages((prev) => [
        ...prev,
        { role: 'bot', content: data?.response },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'bot', content: 'Something went wrong while fetching the response. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => sendQuestion(input.trim());

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = async () => {
    const sessionId = sessionIdRef.current;
    setMessages([WELCOME_MESSAGE]);
    persistSessionId(null);

    if (sessionId) {
      try {
        await resetInsightBotSession(sessionId);
      } catch {
        // best-effort: local conversation is already cleared
      }
    }
  };

  return (
    <div
      className="insight-bot-widget"
      ref={widgetRef}
      style={{ bottom: `${position.bottom}px`, right: `${position.right}px`, cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      {isOpen && (
        <div className="insight-bot-panel" role="dialog" aria-label={`${BOT_DISPLAY_NAME} chat`}>
          <div className="insight-bot-panel__header">
            <span>{BOT_DISPLAY_NAME}</span>
            <div className="insight-bot-panel__header-actions">
              <button
                type="button"
                className="insight-bot-panel__clear"
                onClick={handleClearChat}
                aria-label="Clear chat"
                title="Start a new conversation"
              >
                <RotateCcw size={16} />
              </button>
              <button
                type="button"
                className="insight-bot-panel__close"
                onClick={toggleOpen}
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="insight-bot-panel__messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`insight-bot-message insight-bot-message--${msg.role}`}>
                {msg.role === 'bot' ? (
                  <div className="insight-bot-message__markdown">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {msg.content || 'No response was returned for this query.'}
                    </ReactMarkdown>
                  </div>
                ) : (
                  msg.content
                )}
              </div>
            ))}
            {isLoading && (
              <div className="insight-bot-message insight-bot-message--bot insight-bot-message--loading">
                <Loader2 size={16} className="insight-bot-spinner" />
                {getThinkingText(loadingElapsed)}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="insight-bot-panel__input">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question..."
              rows={1}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className="insight-bot-fab"
        onClick={toggleOpen}
        onMouseDown={handleFabMouseDown}
        aria-label={isOpen ? `Close ${BOT_DISPLAY_NAME} chat` : `Open ${BOT_DISPLAY_NAME} chat`}
        style={{ userSelect: isDragging ? 'none' : 'auto' }}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>
    </div>
  );
}

export default InsightBotWidget;
